import type { AssemblyMateDocumentJSON } from '../../assembly/document'
import type { MateEntityRef } from '../../assembly/definition'
import { normalizeAssemblyNodeId } from '../../assembly/utils/AssemblyNodePath'
import { remapPuppyAssemblyMateDocumentNodeReferences } from '../../export/puppy/remapPuppyAssemblyMateDocument'
import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'
import type { ImportedModelBodyTopologyOccurrenceRebinding } from '../../imported-model/topology/ImportedModelTopologyRepository'
import {
  areImportedBodyBindingIdsEquivalent,
  normalizeImportedBodyBindingId,
  resolveImportedBodyOccurrenceNodeId,
} from '../../imported-model/utils'

/**
 * 项目模型局部替换完成后的 Mate 文档运行时重绑定输入。
 */
export interface RebindProjectModelMateDocumentToImportedResultRuntimeInput {
  /** 已完成包级初次重映射、但仍可能携带包内根的 Mate 文档。 */
  readonly document: AssemblyMateDocumentJSON
  /** compose 刚生成、尚未折叠到项目目标层级的 hierarchy。 */
  readonly generatedHierarchy: GlbHierarchyNode
  /** 已折叠到项目最终零件层级的 hierarchy。 */
  readonly rebasedHierarchy: GlbHierarchyNode
  /** generated body occurrence 到最终零件 occurrence 的一对一映射。 */
  readonly bodyOccurrenceRebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[]
  /** 重绑完成后 Mate 文档自身所属的最终装配节点 ID。 */
  readonly documentAssemblyNodeId: string
  /**
   * 当前切换事务已经确认的旧节点身份到最终 occurrence 的映射。
   *
   * 该映射只接收项目节点、工作装配文档节点等权威身份，不接受名称或遍历位置推断；
   * 写入时继续经过统一冲突检查，避免显式身份与 hierarchy/body 映射互相覆盖。
   */
  readonly explicitNodeAliases?: ReadonlyMap<string, string>
}

/**
 * 向 scoped 节点 alias 表写入确定映射。
 *
 * @param aliasByNodeId alias 输出表。
 * @param sourceNodeId 切换前或包内节点 ID。
 * @param targetNodeId 最终项目节点 ID。
 */
function setScopedProjectModelNodeAlias(
  aliasByNodeId: Record<string, string>,
  sourceNodeId: string | null | undefined,
  targetNodeId: string | null | undefined,
): void {
  const sourceNodeIdValue = normalizeAssemblyNodeId(sourceNodeId?.trim() ?? '')
  const targetNodeIdValue = normalizeAssemblyNodeId(targetNodeId?.trim() ?? '')
  if (!sourceNodeIdValue || !targetNodeIdValue || sourceNodeIdValue === targetNodeIdValue) {
    return
  }

  const currentTargetNodeId = aliasByNodeId[sourceNodeIdValue]
  if (currentTargetNodeId && currentTargetNodeId !== targetNodeIdValue) {
    throw new Error(
      `项目模型 scoped Mate 节点映射冲突：${sourceNodeIdValue} -> ${currentTargetNodeId} / ${targetNodeIdValue}`,
    )
  }
  aliasByNodeId[sourceNodeIdValue] = targetNodeIdValue
}

/**
 * 按 Object3D uuid 对齐 generated 与 rebased hierarchy 的装配节点 ID。
 *
 * @param generatedHierarchy compose 原始 hierarchy。
 * @param rebasedHierarchy 最终项目 hierarchy。
 * @param aliasByNodeId alias 输出表。
 */
function collectScopedProjectModelHierarchyNodeAliases(
  generatedHierarchy: GlbHierarchyNode,
  rebasedHierarchy: GlbHierarchyNode,
  aliasByNodeId: Record<string, string>,
): void {
  const generatedNodeByObjectId = new Map<string, GlbHierarchyNode>()
  const collectGeneratedNode = (node: GlbHierarchyNode): void => {
    generatedNodeByObjectId.set(node.objectId, node)
    node.children.forEach(collectGeneratedNode)
  }
  collectGeneratedNode(generatedHierarchy)

  const collectRebasedNode = (node: GlbHierarchyNode): void => {
    const generatedNode = generatedNodeByObjectId.get(node.objectId)
    setScopedProjectModelNodeAlias(
      aliasByNodeId,
      generatedNode?.meta?.assemblyNodeId,
      node.meta?.assemblyNodeId,
    )
    node.children.forEach(collectRebasedNode)
  }
  collectRebasedNode(rebasedHierarchy)
}

/**
 * 按 bodyBindingId 解析唯一的 occurrence 重绑定。
 *
 * @param bodyBindingId Mate topologyRef 当前保存的 bodyBindingId。
 * @param rebindings 本次 scoped 切换的 body occurrence 映射。
 * @param preferredTargetAssemblyNodeId 实体经 hierarchy alias 对齐后的目标 occurrence。
 * @returns 唯一重绑定；未命中返回 null，歧义时抛错。
 */
function resolveScopedProjectModelBodyOccurrenceRebinding(
  bodyBindingId: string | null | undefined,
  rebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[],
  preferredTargetAssemblyNodeId?: string | null,
): ImportedModelBodyTopologyOccurrenceRebinding | null {
  const normalizedBodyBindingId = normalizeImportedBodyBindingId(bodyBindingId)
  if (!normalizedBodyBindingId) {
    return null
  }

  const exactCandidates = rebindings.filter((rebinding) => (
    normalizeImportedBodyBindingId(rebinding.sourceBodyBindingId) === normalizedBodyBindingId
    || normalizeImportedBodyBindingId(rebinding.targetBodyBindingId) === normalizedBodyBindingId
  ))
  const candidates = exactCandidates.length > 0
    ? exactCandidates
    : rebindings.filter((rebinding) => (
        areImportedBodyBindingIdsEquivalent(rebinding.sourceBodyBindingId, normalizedBodyBindingId)
        || areImportedBodyBindingIdsEquivalent(rebinding.targetBodyBindingId, normalizedBodyBindingId)
      ))
  const normalizedPreferredTargetAssemblyNodeId = normalizeAssemblyNodeId(
    preferredTargetAssemblyNodeId?.trim() ?? '',
  )
  const ownerCandidates = normalizedPreferredTargetAssemblyNodeId
    ? candidates.filter((candidate) => (
        normalizeAssemblyNodeId(candidate.targetAssemblyNodeId)
        === normalizedPreferredTargetAssemblyNodeId
      ))
    : candidates
  const resolvedCandidates = ownerCandidates.length > 0 ? ownerCandidates : candidates
  const targetKeys = new Set(resolvedCandidates.map((candidate) => (
    `${candidate.targetAssemblyNodeId}\u0000${candidate.targetBodyBindingId}`
  )))
  if (targetKeys.size > 1) {
    throw new Error(`项目模型 scoped Mate body occurrence 映射存在歧义：${bodyBindingId}`)
  }
  return resolvedCandidates[0] ?? null
}

/**
 * 根据实体 topologyRef 收集其所有装配身份字段的最终 alias。
 *
 * @param entity Mate 实体。
 * @param rebindings body occurrence 映射。
 * @param aliasByNodeId alias 输出表。
 */
function collectScopedProjectModelMateEntityAliases(
  entity: MateEntityRef | null | undefined,
  rebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[],
  aliasByNodeId: Record<string, string>,
): void {
  if (!entity) {
    return
  }
  const normalizedEntityAssemblyNodeId = normalizeAssemblyNodeId(
    entity.topologyRef?.assemblyNodeId ?? entity.assemblyNodeId,
  )
  const targetEntityAssemblyNodeId = aliasByNodeId[normalizedEntityAssemblyNodeId]
    ?? normalizedEntityAssemblyNodeId
  const rebinding = resolveScopedProjectModelBodyOccurrenceRebinding(
    entity.topologyRef?.bodyBindingId,
    rebindings,
    targetEntityAssemblyNodeId,
  )
  if (rebinding) {
    setScopedProjectModelNodeAlias(aliasByNodeId, entity.assemblyNodeId, rebinding.targetAssemblyNodeId)
    setScopedProjectModelNodeAlias(aliasByNodeId, entity.componentInstanceId, rebinding.targetAssemblyNodeId)
    setScopedProjectModelNodeAlias(
      aliasByNodeId,
      entity.topologyRef?.assemblyNodeId,
      rebinding.targetAssemblyNodeId,
    )
  }

  entity.referenceDependency?.driverTopologyRefs.forEach((topologyRef) => {
    const normalizedDriverAssemblyNodeId = normalizeAssemblyNodeId(topologyRef.assemblyNodeId)
    const driverRebinding = resolveScopedProjectModelBodyOccurrenceRebinding(
      topologyRef.bodyBindingId,
      rebindings,
      aliasByNodeId[normalizedDriverAssemblyNodeId] ?? normalizedDriverAssemblyNodeId,
    )
    if (driverRebinding) {
      setScopedProjectModelNodeAlias(
        aliasByNodeId,
        topologyRef.assemblyNodeId,
        driverRebinding.targetAssemblyNodeId,
      )
    }
  })
}

/**
 * 创建 scoped Mate 文档到最终项目 hierarchy 的节点 alias。
 *
 * @param input 运行时重绑定输入。
 * @returns 只包含确定性前后关系的 alias 表。
 */
function createScopedProjectModelMateRuntimeAliasByNodeId(
  input: RebindProjectModelMateDocumentToImportedResultRuntimeInput,
): Record<string, string> {
  const aliasByNodeId: Record<string, string> = {}
  input.explicitNodeAliases?.forEach((targetNodeId, sourceNodeId) => {
    setScopedProjectModelNodeAlias(aliasByNodeId, sourceNodeId, targetNodeId)
  })
  setScopedProjectModelNodeAlias(
    aliasByNodeId,
    input.document.assemblyId,
    input.documentAssemblyNodeId,
  )
  collectScopedProjectModelHierarchyNodeAliases(
    input.generatedHierarchy,
    input.rebasedHierarchy,
    aliasByNodeId,
  )
  input.bodyOccurrenceRebindings.forEach((rebinding) => {
    setScopedProjectModelNodeAlias(
      aliasByNodeId,
      resolveImportedBodyOccurrenceNodeId(rebinding.sourceBodyBindingId),
      rebinding.targetAssemblyNodeId,
    )
  })
  input.document.mates.forEach((mate) => {
    collectScopedProjectModelMateEntityAliases(
      mate.firstEntity,
      input.bodyOccurrenceRebindings,
      aliasByNodeId,
    )
    collectScopedProjectModelMateEntityAliases(
      mate.secondEntity,
      input.bodyOccurrenceRebindings,
      aliasByNodeId,
    )
    collectScopedProjectModelMateEntityAliases(
      mate.referenceEntity,
      input.bodyOccurrenceRebindings,
      aliasByNodeId,
    )
  })
  return aliasByNodeId
}

/**
 * 把 topologyRef 精确同步到最终 body occurrence。
 *
 * @param topologyRef 当前 topologyRef。
 * @param rebindings body occurrence 映射。
 * @returns 使用最终零件 owner/bodyBindingId 的 topologyRef。
 */
function rebindScopedProjectModelTopologyRef(
  topologyRef: NonNullable<MateEntityRef['topologyRef']>,
  rebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[],
): NonNullable<MateEntityRef['topologyRef']> {
  const rebinding = resolveScopedProjectModelBodyOccurrenceRebinding(
    topologyRef.bodyBindingId,
    rebindings,
    topologyRef.assemblyNodeId,
  )
  return rebinding
    ? {
        ...topologyRef,
        assemblyNodeId: rebinding.targetAssemblyNodeId,
        bodyBindingId: rebinding.targetBodyBindingId,
      }
    : topologyRef
}

/**
 * 把 Mate 实体及其派生 topology 依赖同步到最终 body occurrence。
 *
 * @param entity 已完成节点 alias 重映射的 Mate 实体。
 * @param rebindings body occurrence 映射。
 * @returns 最终运行时 Mate 实体。
 */
function rebindScopedProjectModelMateEntity(
  entity: MateEntityRef,
  rebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[],
): MateEntityRef {
  const entityRebinding = resolveScopedProjectModelBodyOccurrenceRebinding(
    entity.topologyRef?.bodyBindingId,
    rebindings,
    entity.topologyRef?.assemblyNodeId ?? entity.assemblyNodeId,
  )
  return {
    ...entity,
    ...(entityRebinding
      ? {
          assemblyNodeId: entityRebinding.targetAssemblyNodeId,
          componentInstanceId: entityRebinding.targetAssemblyNodeId,
        }
      : {}),
    ...(entity.topologyRef
      ? {
          topologyRef: rebindScopedProjectModelTopologyRef(entity.topologyRef, rebindings),
        }
      : {}),
    ...(entity.referenceDependency
      ? {
          referenceDependency: {
            ...entity.referenceDependency,
            driverTopologyRefs: entity.referenceDependency.driverTopologyRefs.map((topologyRef) => (
              rebindScopedProjectModelTopologyRef(topologyRef, rebindings)
            )),
          },
        }
      : {}),
  }
}

/**
 * 将 scoped 节点包 Mate 文档对齐到最终 imported-result runtime hierarchy。
 *
 * 设计说明：
 * 1. 包级初次 remap 发生在 scoped hierarchy 最终折叠之前，实体可能仍是 `目标根/包内根/零件`；
 * 2. 设计树 Mate in assembly、高亮和移动共同依赖实体的最终组件 ID；
 * 3. 本入口只消费本次切换产生的一对一 hierarchy/body 映射，不修改普通加载或全局身份匹配规则。
 *
 * @param input scoped Mate 文档与最终运行时映射。
 * @returns 可按原文档所有权交给根恢复或 scoped 恢复入口的最终 Mate 文档。
 */
export function rebindProjectModelMateDocumentToImportedResultRuntime(
  input: RebindProjectModelMateDocumentToImportedResultRuntimeInput,
): AssemblyMateDocumentJSON {
  const aliasByNodeId = createScopedProjectModelMateRuntimeAliasByNodeId(input)
  const remappedDocument = remapPuppyAssemblyMateDocumentNodeReferences({
    mates: input.document,
    aliasByNodeId,
  })

  return {
    ...remappedDocument,
    assemblyId: normalizeAssemblyNodeId(input.documentAssemblyNodeId),
    mates: remappedDocument.mates.map((mate) => ({
      ...mate,
      firstEntity: rebindScopedProjectModelMateEntity(
        mate.firstEntity,
        input.bodyOccurrenceRebindings,
      ),
      secondEntity: rebindScopedProjectModelMateEntity(
        mate.secondEntity,
        input.bodyOccurrenceRebindings,
      ),
      ...(mate.referenceEntity
        ? {
            referenceEntity: rebindScopedProjectModelMateEntity(
              mate.referenceEntity,
              input.bodyOccurrenceRebindings,
            ),
          }
        : {}),
    })),
  }
}
